import { z } from 'zod';
import type { BlockNode, BlockNodeWithMedia, UrlMediaSource } from './ir';
import { checkRawBounds, createBlockSchema, mediaSourceSchema, surfaceSchema, themeSchema } from './ir.schema';

// THE AUTHORING DOC — the mutable D1-side artifact (SPEC §2), and the gatekeeper for the
// `experiences.document` column. It is NOT the wire bundle and is never sent to an SDK: `publish`
// compiles it. That split is what lets this shape churn weekly while no pinned binary notices.
//
// It lives HERE, not in `packages/editor`, because this is where every Zod gatekeeper lives and
// `apps/api` — which must validate it at its trust boundary — already depends on this package.
// `packages/editor` type-imports it back out, so there is exactly ONE definition of the shape.
// Two hand-maintained copies of a trust-boundary payload is the bug class `coverage.test.ts`
// already exists to prevent.
//
// Zod is fine here in a way it is NOT in `ir.ts`/`measure.ts`: those are zero-dep because four SDKs
// port them line by line. Nothing ports the authoring doc — it never leaves the server.

export const advanceRuleSchema = z.strictObject({
  when: z.literal('timeout'),
  ms: z.number().int().positive(),
});

export interface ManagedMediaSource {
  kind: 'asset';
  asset_id: string;
}

export type AuthoringMediaSource = UrlMediaSource | ManagedMediaSource;
export type AuthoringBlockNode = BlockNodeWithMedia<AuthoringMediaSource>;

export interface ResolvedAuthoringAsset {
  url: string;
  contentType: string;
}

export type AuthoringAssetResolver = (assetId: string) => ResolvedAuthoringAsset | undefined;

/** Resolve editor-only identity at the single authoring-to-wire boundary. URL sources pass through
 *  byte-for-byte; managed identity fails closed when ownership/availability or media kind cannot be
 *  established by the caller's app-scoped resolver. */
export function resolveAuthoringBlock(
  root: AuthoringBlockNode,
  resolveAsset?: AuthoringAssetResolver
): BlockNode {
  const fallback = root.fallback ? resolveAuthoringBlock(root.fallback, resolveAsset) : undefined;
  const shared = fallback ? { fallback } : {};

  if (root.block === 'image' || root.block === 'video') {
    if (root.source.kind === 'url') return { ...root, ...shared } as BlockNode;
    if (!resolveAsset) {
      throw new Error(`${root.block} "${root.id}" needs asset "${root.source.asset_id}", but no asset resolver was provided`);
    }
    const asset = resolveAsset(root.source.asset_id);
    if (!asset) throw new Error(`${root.block} "${root.id}" points at unavailable asset "${root.source.asset_id}"`);
    const expected = root.block === 'image' ? 'image/' : 'video/';
    if (!asset.contentType.startsWith(expected)) {
      throw new Error(`${root.block} "${root.id}" points at ${asset.contentType} asset "${root.source.asset_id}"`);
    }
    return { ...root, ...shared, source: { kind: 'url', url: asset.url } } as BlockNode;
  }

  if ('children' in root) {
    return {
      ...root,
      ...shared,
      children: root.children.map((child) => resolveAuthoringBlock(child, resolveAsset)),
    } as BlockNode;
  }
  if (root.block === 'repeat') {
    return { ...root, ...shared, template: resolveAuthoringBlock(root.template, resolveAsset) } as BlockNode;
  }
  return { ...root, ...shared } as BlockNode;
}

export const authoringMediaSourceSchema: z.ZodType<AuthoringMediaSource> = z.union([
  mediaSourceSchema,
  z.strictObject({ kind: z.literal('asset'), asset_id: z.string().min(1) }),
]);
export const authoringBlockSchema: z.ZodType<AuthoringBlockNode> = createBlockSchema(authoringMediaSourceSchema);

export const stepSchema = z.strictObject({
  id: z.string().min(1),
  name: z.string().min(1),
  /** Per-step, not per-doc: "fullscreen takeover → modal" is a normal two-step flow. */
  surface: surfaceSchema,
  // Bounds on the RAW input before the recursive parse — a draft arrives over the network too, and
  // a hostile 2000-deep tree blows the stack on the way in. Same guard experienceDocumentSchema uses.
  root: z.unknown().superRefine(checkRawBounds).pipe(authoringBlockSchema),
  advance: z.array(advanceRuleSchema).optional(),
});

// ---------------------------------------------------------------------------- variants (ADR 0015)

/** The variant key `'default'` is RESERVED: it names the doc's own top-level `steps`, so an
 *  allocation can reference the control arm without the doc storing its default twice. */
export const DEFAULT_VARIANT_KEY = 'default';

/** One alternative to the doc's `steps` — a FULL step-tree, not a patch (a patch grammar would be a
 *  fifth mini-parser, and the wire never carries a variant anyway: publish compiles each into its
 *  own `Flow`, qualify ships exactly one). The `key` is the stable analytics identity half of
 *  ADR 0016 (`experiment_key` is the other); the `name` is what the builder shows. */
export const variantSchema = z.strictObject({
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, 'a variant key is lowercase letters, digits and underscores'),
  name: z.string().min(1).max(80),
  steps: z.array(stepSchema).min(1).max(20),
});
export type Variant = z.infer<typeof variantSchema>;

// ---------------------------------------------------------------------------- the migration runner

/** The shape `authoringDocSchema` accepts today. Bumping this is what makes an old doc old. */
export const DOC_VERSION = 3;

/** `MIGRATIONS[n]` takes a doc at `doc_version: n` and returns it at `n + 1`. */
const MIGRATIONS: Record<number, (doc: Record<string, unknown>) => Record<string, unknown>> = {
  // v2 broadens media sources without rewriting existing URL documents. The version bump records
  // the new authoring contract; managed identity is only written by an explicit library choice.
  1: (doc) => doc,
  // v3 adds optional `variants` (ADR 0015). Absence IS the migration: a v2 doc is a v3 doc with no
  // variants, byte-for-byte — nothing is rewritten, same precedent as v2.
  2: (doc) => doc,
};

/** LAZY: a doc migrates when it is read, and is persisted in the new shape the next time it is
 *  saved. Runs in front of every parse (it is the schema's own preprocess), so there is no read path
 *  that can forget it — including the compiler's, which is the one that matters.
 *
 *  Anything it cannot handle is passed through UNTOUCHED for the schema to reject. A half-migrated
 *  doc must never exist: a loud 400 beats a document that silently lost a step. */
export function migrateDoc(doc: unknown): unknown {
  if (typeof doc !== 'object' || doc === null || Array.isArray(doc)) return doc;

  let current = doc as Record<string, unknown>;
  let version = current['doc_version'];
  if (typeof version !== 'number') return current;

  while (version < DOC_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) return current; // a hole in the chain — never guess, let the gate reject it
    current = { ...step(current), doc_version: version + 1 };
    version += 1;
  }
  return current;
}

/** Structural, NOT the full publish gate. Deliberately does not check that colour/font tokens
 *  RESOLVE: a draft may reference a token the author is one keystroke away from creating, and
 *  refusing to save that is hostile. `experienceDocumentSchema` remains the law at publish — which
 *  is the only moment it matters (p3-authoring-dashboard.md §4: "the editor's inline checks are UX,
 *  the schema is the law"). A doc can therefore SAVE but not PUBLISH. That asymmetry is the design. */
export const authoringDocSchema = z.preprocess(migrateDoc, z.strictObject({
  /** Lazily migrated on read — see migrateDoc above. */
  doc_version: z.literal(DOC_VERSION),
  /** ONE theme for the whole doc, not one per step: steps are a single authored flow, and N drifting
   *  copies of the token dictionary is a bug generator. Variants share it for the same reason — two
   *  arms of one experiment differing by an unrelated token drift is a confounded result, not a
   *  design freedom. */
  theme: themeSchema,
  // ponytail: 20 is a DoS bound, not a product opinion. Raise it when someone actually hits it.
  steps: z.array(stepSchema).min(1).max(20),
  /** Alternative step-sets (ADR 0015). The top-level `steps` is the implicit `default` variant, so a
   *  doc with no `variants` is exactly the pre-v3 doc. ponytail: 7 is a DoS bound (8 arms total). */
  variants: z.array(variantSchema).max(7).optional(),
  /** Layer names, keyed by node id. EDITOR-ONLY, and this is exactly why the authoring doc exists:
   *  `BlockBase` has no `name` and is not getting one — a field whose only consumer is a dashboard
   *  tree would be dead weight in every payload, on every device, forever. Publish compiles
   *  `steps[i]` into an `ExperienceDocument`, which has no room for this, so it is dropped at the
   *  boundary by construction rather than by a rule someone has to remember. A node with no entry
   *  renders a derived summary in the tree ("vertical stack", "text: Welcome back"). One map serves
   *  every variant too: node ids are unique per doc (paste/duplicate regenerates them). */
  labels: z.record(z.string(), z.string().min(1).max(80)).optional(),
}).superRefine((doc, ctx) => {
  if (!doc.variants) return;
  const seen = new Set<string>();
  doc.variants.forEach((variant, index) => {
    if (variant.key === DEFAULT_VARIANT_KEY) {
      ctx.addIssue({
        code: 'custom',
        path: ['variants', index, 'key'],
        message: `"${DEFAULT_VARIANT_KEY}" is reserved for the doc's own steps`,
      });
    }
    if (seen.has(variant.key)) {
      ctx.addIssue({
        code: 'custom',
        path: ['variants', index, 'key'],
        message: `variant key "${variant.key}" appears twice`,
      });
    }
    seen.add(variant.key);
  });
}));

export type AuthoringDoc = z.infer<typeof authoringDocSchema>;
export type Step = AuthoringDoc['steps'][number];
export type AdvanceRule = NonNullable<Step['advance']>[number];
